import { useState } from "react";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";

export function PostCallForm({ profile, onClose, onSaved, existingOpportunity = null, calloutLimitReached = false }) {
  const [form, setForm] = useState(() => {
    const ex = existingOpportunity;
    return {
      type:                 ex?.type               || 'exhibition',
      title:                ex?.title              || '',
      org_name:             ex?.org_name           || profile?.display_name || profile?.full_name || '',
      location:             ex?.location           || '',
      state:                ex?.state              || '',
      theme:                ex?.theme              || '',
      deadline:             ex?.deadline           || '',
      deadline_time:        ex?.deadline_time      || '5:00 pm AEST',
      entry_fee_type:       ex?.entry_fee_type     || 'free',
      entry_fee_amount:     ex?.entry_fee_amount != null ? String(ex.entry_fee_amount) : '',
      prize:                ex?.prize              || '',
      commission:           ex?.commission         || '',
      eligibility:          ex?.eligibility        || 'Australian citizens & permanent residents',
      abn_required:         ex?.abn_required       ?? true,
      tag:                  ex?.tag                || 'open',
      consignment_offered:  ex?.consignment_offered  || false,
      consignment_duration: ex?.consignment_duration != null ? String(ex.consignment_duration) : '',
      return_shipping:      ex?.return_shipping    || '',
      payment_terms:        ex?.payment_terms      || '',
      damage_policy:        ex?.damage_policy      || '',
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!profile?.id) { setError('You must be logged in as a gallery to post.'); return; }
    if (!existingOpportunity && calloutLimitReached) { setError("You've reached your 3 open call limit. Upgrade to Established for unlimited open calls."); return; }
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.deadline) { setError('Deadline is required.'); return; }
    setSaving(true);
    setError(null);
    const payload = {
      type:               form.type,
      title:              form.title.trim(),
      org_name:           form.org_name.trim() || profile?.display_name || '',
      location:           form.location.trim() || null,
      state:              form.state || null,
      theme:              form.theme.trim() || null,
      deadline:           form.deadline,
      deadline_time:      form.deadline_time,
      entry_fee_type:     form.entry_fee_type,
      entry_fee_amount:   form.entry_fee_type === 'paid' && form.entry_fee_amount ? parseFloat(form.entry_fee_amount) : null,
      prize:              form.prize.trim() || null,
      commission:         form.commission.trim() || null,
      eligibility:        form.eligibility.trim() || null,
      abn_required:       form.abn_required,
      tag:                form.tag,
      consignment_offered:   form.consignment_offered,
      consignment_duration:  form.consignment_offered && form.consignment_duration ? parseInt(form.consignment_duration) : null,
      return_shipping:       form.consignment_offered ? form.return_shipping || null : null,
      payment_terms:         form.consignment_offered ? form.payment_terms.trim() || null : null,
      damage_policy:         form.consignment_offered ? form.damage_policy || null : null,
    };
    let err;
    try {
      if (existingOpportunity) {
        ({ error: err } = await supabase.from('opportunities').update(payload).eq('id', existingOpportunity.id));
      } else {
        ({ error: err } = await supabase.from('opportunities').insert({ gallery_id: profile.id, status: 'active', ...payload }));
      }
    } catch (e) {
      setSaving(false);
      setError(e.message || 'Unexpected error — check the console.');
      return;
    }
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
    onClose();
  };

  const inp = { background:C.cream, border:`1px solid ${C.sand}`, borderRadius:6, padding:"10px 12px", fontSize:".84rem", color:C.bark, width:"100%", boxSizing:"border-box", outline:"none", fontFamily:"inherit" };
  const lbl = { fontSize:".68rem", fontFamily:"'DM Mono',monospace", letterSpacing:"1px", textTransform:"uppercase", color:C.muted, display:"block", marginBottom:5 };
  const row = { marginBottom:16 };

  return (
    <div style={{ position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",background:"rgba(40,30,20,.55)",overflowY:"auto",padding:"24px 16px" }}>
      <div style={{ background:C.parch,borderRadius:12,width:"100%",maxWidth:560,padding:"28px 24px",boxShadow:"0 8px 40px rgba(0,0,0,.18)" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24 }}>
          <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.4rem",fontWeight:400,color:C.bark,margin:0 }}>{existingOpportunity ? 'Edit Open Call' : 'Post Open Call'}</h2>
          <button onClick={onClose} style={{ background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:C.muted,lineHeight:1,padding:4 }}>✕</button>
        </div>

        <div style={row}>
          <label style={lbl}>Type</label>
          <div style={{ display:"flex",gap:8 }}>
            {['exhibition','grant','residency'].map(t=>(
              <button key={t} onClick={()=>set('type',t)} style={{ flex:1,padding:"8px 4px",borderRadius:6,border:`1px solid ${form.type===t?C.clay:C.sand}`,background:form.type===t?C.clay:"transparent",color:form.type===t?"#fff":C.bark,fontSize:".72rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",cursor:"pointer" }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div style={row}>
          <label style={lbl}>Title *</label>
          <input style={inp} value={form.title} onChange={e=>set('title',e.target.value)} placeholder="e.g. Surface Tensions"/>
        </div>

        <div style={row}>
          <label style={lbl}>Organisation name</label>
          <input style={inp} value={form.org_name} onChange={e=>set('org_name',e.target.value)} placeholder="e.g. Gertrude Contemporary"/>
        </div>

        <div style={{ display:"flex",gap:12,marginBottom:16 }}>
          <div style={{ flex:2 }}>
            <label style={lbl}>Location</label>
            <input style={inp} value={form.location} onChange={e=>set('location',e.target.value)} placeholder="e.g. Fitzroy, VIC"/>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>State</label>
            <select style={inp} value={form.state} onChange={e=>set('state',e.target.value)}>
              <option value="">Any</option>
              {['VIC','NSW','QLD','WA','SA','TAS','ACT','NT'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div style={row}>
          <label style={lbl}>Theme / description</label>
          <textarea style={{ ...inp, minHeight:80, resize:"vertical" }} value={form.theme} onChange={e=>set('theme',e.target.value)} placeholder="Brief description of the opportunity…"/>
        </div>

        <div style={{ display:"flex",gap:12,marginBottom:16 }}>
          <div style={{ flex:2 }}>
            <label style={lbl}>Deadline *</label>
            <input style={inp} type="date" value={form.deadline} onChange={e=>set('deadline',e.target.value)}/>
          </div>
          <div style={{ flex:2 }}>
            <label style={lbl}>Deadline time</label>
            <input style={inp} value={form.deadline_time} onChange={e=>set('deadline_time',e.target.value)} placeholder="5:00 pm AEST"/>
          </div>
        </div>

        <div style={{ display:"flex",gap:12,marginBottom:16 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>Entry fee</label>
            <select style={inp} value={form.entry_fee_type} onChange={e=>set('entry_fee_type',e.target.value)}>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          {form.entry_fee_type==='paid'&&(
            <div style={{ flex:1 }}>
              <label style={lbl}>Amount (AUD)</label>
              <input style={inp} type="number" min="0" step="0.01" value={form.entry_fee_amount} onChange={e=>set('entry_fee_amount',e.target.value)} placeholder="25"/>
            </div>
          )}
        </div>

        <div style={{ display:"flex",gap:12,marginBottom:16 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>Prize / funding</label>
            <input style={inp} value={form.prize} onChange={e=>set('prize',e.target.value)} placeholder="e.g. $3,000 acquisitive prize"/>
          </div>
          <div style={{ flex:1 }}>
            <label style={lbl}>Commission</label>
            <input style={inp} value={form.commission} onChange={e=>set('commission',e.target.value)} placeholder="e.g. 30%"/>
          </div>
        </div>

        {form.type === 'exhibition' && (
          <div style={{ ...row, borderTop: `1px solid ${C.sand}`, paddingTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', color: C.bark, cursor: 'pointer', marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={form.consignment_offered}
                onChange={e => set('consignment_offered', e.target.checked)}
              />
              Offer consignment agreement
            </label>

            {form.consignment_offered && (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Duration (days)</label>
                    <input style={inp} type="number" min="0" value={form.consignment_duration} onChange={e => set('consignment_duration', e.target.value)} placeholder="e.g. 30"/>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Insurance responsibility</label>
                    <select style={inp} value={form.damage_policy} onChange={e => set('damage_policy', e.target.value)}>
                      <option value="">Select…</option>
                      <option value="artist">Artist</option>
                      <option value="gallery">Gallery</option>
                      <option value="shared">Shared</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Shipping responsibility</label>
                    <select style={inp} value={form.return_shipping} onChange={e => set('return_shipping', e.target.value)}>
                      <option value="">Select…</option>
                      <option value="artist">Artist</option>
                      <option value="gallery">Gallery</option>
                      <option value="shared">Shared</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Payment terms</label>
                    <input style={inp} value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} placeholder="e.g. 30 days after sale"/>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div style={row}>
          <label style={lbl}>Eligibility</label>
          <input style={inp} value={form.eligibility} onChange={e=>set('eligibility',e.target.value)} placeholder="e.g. Australian citizens & permanent residents"/>
        </div>

        <div style={{ display:"flex",gap:24,marginBottom:20,alignItems:"center" }}>
          <label style={{ display:"flex",alignItems:"center",gap:8,fontSize:".82rem",color:C.bark,cursor:"pointer" }}>
            <input type="checkbox" checked={form.abn_required} onChange={e=>set('abn_required',e.target.checked)}/>
            ABN required
          </label>
          <div style={{ flex:1 }}>
            <select style={{ ...inp, fontSize:".72rem" }} value={form.tag} onChange={e=>set('tag',e.target.value)}>
              <option value="open">Open</option>
              <option value="closing">Closing soon</option>
              <option value="featured">Featured</option>
            </select>
          </div>
        </div>

        {error&&<div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:6,padding:"10px 14px",fontSize:".82rem",color:C.rose,marginBottom:16 }}>⚠ {error}</div>}

        <div style={{ display:"flex",gap:10 }}>
          <button className="btn-dk" style={{ flex:1,padding:"12px" }} onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-p" style={{ flex:2,padding:"12px" }} onClick={handleSubmit} disabled={saving}>{saving?(existingOpportunity?"Saving…":"Posting…"):(existingOpportunity?"Save changes":"Publish open call")}</button>
        </div>
      </div>
    </div>
  );
}
